import axios from 'axios';

/**
 * Helper to recursively convert keys to snake_case
 */
export function keysToSnake(obj: any): any {
  if (Array.isArray(obj)) {
    return obj.map(v => keysToSnake(v));
  } else if (obj !== null && typeof obj === 'object' && obj.constructor === Object) {
    return Object.keys(obj).reduce((result, key) => {
      const snakeKey = key.replace(/([A-Z])/g, '_$1').toLowerCase();
      result[snakeKey] = keysToSnake(obj[key]);
      return result;
    }, {} as any);
  }
  return obj;
}

/**
 * Helper to recursively convert keys to camelCase
 */
export function keysToCamel(obj: any): any {
  if (Array.isArray(obj)) {
    return obj.map(v => keysToCamel(v));
  } else if (obj !== null && typeof obj === 'object' && obj.constructor === Object) {
    return Object.keys(obj).reduce((result, key) => {
      const camelKey = key.replace(/(_\w)/g, m => m[1].toUpperCase());
      result[camelKey] = keysToCamel(obj[key]);
      return result;
    }, {} as any);
  }
  return obj;
}

// Determine the dynamic API base path based on subdirectory installation
let calculatedURL = '/api';
const pathname = window.location.pathname; // e.g., "/mastercoifa/orders"
const segments = pathname.split('/').filter(Boolean);
const knownRoutes = ['orders', 'settings', 'products', 'categories', 'help', 'reports', 'login'];

if (segments.length > 0) {
  // If the first segment is not a frontend React Router view, it is likely the subdirectory
  if (!knownRoutes.includes(segments[0].toLowerCase())) {
    calculatedURL = `/${segments[0]}/api`;
  }
}

// Create custom Axios instance
const envBaseURL = typeof import.meta !== 'undefined' && 'env' in import.meta 
  ? (import.meta as any).env?.VITE_API_BASE_URL 
  : undefined;

const api = axios.create({
  baseURL: envBaseURL || calculatedURL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 10000,
});

// Request Interceptor: convert payload camelCase keys to snake_case before calling raw endpoints
api.interceptors.request.use(
  (config) => {
    // If there is token stored, append it
    const token = localStorage.getItem('token');
    if (token && config.headers) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }

    if (config.data && !(config.data instanceof FormData)) {
      config.data = keysToSnake(config.data);
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response Interceptor: convert retrieved payload keys back from snake_case to camelCase
api.interceptors.response.use(
  (response) => {
    if (response.data) {
      response.data = keysToCamel(response.data);
    }
    return response;
  },
  (error) => {
    // Check for 401 Unauthorized errors
    if (error.response?.status === 401) {
      localStorage.removeItem('mc_current_user');
      localStorage.removeItem('mc_active_operator');
      localStorage.removeItem('token');

      const segments = window.location.pathname.split('/').filter(Boolean);
      const knownRoutes = ['orders', 'settings', 'products', 'categories', 'help', 'reports', 'login', 'projetor'];
      let loginPath = '/login';
      if (segments.length > 0 && !knownRoutes.includes(segments[0].toLowerCase())) {
        loginPath = `/${segments[0]}/login`;
      }

      if (!window.location.pathname.endsWith('/login') && !window.location.pathname.endsWith('/projetor')) {
        window.location.href = loginPath;
      }
    }

    // Graceful error isolation
    const serverData = error.response?.data;
    let descriptiveError = 'Erro de comunicação com o servidor de autenticação.';
    if (serverData) {
      if (serverData.error) {
        if (typeof serverData.error === 'object' && serverData.error.message) {
          descriptiveError = serverData.error.message;
        } else {
          descriptiveError = String(serverData.error);
        }
        if (serverData.details) {
          descriptiveError += ` (${serverData.details})`;
        }
      } else if (serverData.message) {
        descriptiveError = String(serverData.message);
      }
    } else if (error.message) {
      descriptiveError = error.message;
    }

    const customError: any = new Error(descriptiveError);
    customError.response = error.response;
    return Promise.reject(customError);
  }
);

export default api;
